import { Component, EventEmitter, Input, Output, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LunarApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/lunar/lunar-api';
import { AdviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/advice/advice-api';
import { LunarDayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/lunar/lunar-day.response';
import { PremiumToastComponent } from '../../../@commons/@toasts/premium-toast/premium-toast.component';
import { PremiumService } from '../../../@commons/@services/front/premium.service';
import { LockIconComponent } from '../../../@icons/lock-icon.component';
import { SkeletonLoaderComponent } from '../../../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';

const INFLUENCES = ['leaf', 'flower', 'fruit', 'root'] as const;
type Influence = (typeof INFLUENCES)[number];

/** Événements qui font un jour de repos — mêmes valeurs que le back. */
const NEUTRAL_EVENTS = [
  'ascending_node',
  'descending_node',
  'perigee',
  'apogee',
];

export interface LunarDayView {
  date: string;
  weekday: string;
  dayNumber: string;
  /** « lundi 3 août », dans la langue de l'utilisateur. */
  fullLabel: string;
  influence: Influence | null;
  moonPath: string;
  moonPhase: string;
  moonIllumination: number | null;
  events: string[];
  neutral: boolean;
  isToday: boolean;
  /** Flouté pour un compte gratuit : seul aujourd'hui est net. */
  locked: boolean;
}

/**
 * « Ta semaine lunaire » — carte en tête du calendrier.
 *
 * Elle vit ici et non sur un écran à part : le calendrier est déjà l'endroit
 * où l'on regarde quand semer et quand récolter. Le jour lunaire y répond à la
 * même question.
 *
 * Gratuit : le jour courant est NET, construit depuis le snapshot du payload
 * des conseils. Les six suivants sont floutés. L'endpoint de la semaine est
 * réservé aux abonnés, on ne l'appelle donc jamais pour rien — jamais de 403,
 * jamais de message d'erreur.
 */
@Component({
  selector: 'vw-lunar-week',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    PremiumToastComponent,
    LockIconComponent,
    SkeletonLoaderComponent,
  ],
  templateUrl: './lunar-week.component.html',
  styleUrl: './lunar-week.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: LunarApi, useClass: LunarApi },
    { provide: AdviceApi, useClass: AdviceApi },
  ],
})
export class LunarWeekComponent {
  readonly premiumService = inject(PremiumService);
  private readonly lunarApi = inject(LunarApi);
  private readonly authApi = new AuthApi();
  private readonly adviceApi = inject(AdviceApi);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  /**
   * Embarqué dans la page de détail lunaire.
   *
   * Le pied de carte disparaît alors : la page qui l'accueille porte déjà les
   * gestes du jour et son propre appel au premium. Deux invitations pour la
   * même chose se neutralisent.
   */
  @Input() embedded = false;

  /** Jour retenu, pour que la page affiche les gestes correspondants. */
  @Output() daySelected = new EventEmitter<LunarDayView | null>();

  days: LunarDayView[] = [];
  selected: LunarDayView | null = null;
  isLoading = true;
  premiumPaywallOpen = false;
  /** Cultures de l'utilisateur que l'influence du jour concerne. */
  matchedCount = 0;

  /** Mode déjà chargé, pour ne pas recharger deux fois la même chose. */
  private loadedFor: boolean | null = null;

  constructor() {
    // Un `effect` et non `ngOnInit` : l'abonnement n'est pas forcément connu au
    // moment où l'écran s'ouvre — le jeton arrive du stockage natif de façon
    // asynchrone. Lire la valeur UNE fois figeait la carte en version
    // verrouillée, et il fallait quitter l'écran pour la corriger.
    //
    // Ici, dès que l'accès bascule (abonnement ou interrupteur du premium), la
    // semaine se recharge d'elle-même.
    effect(() => {
      const premium = this.premiumService.hasPremiumAccess();
      if (this.loadedFor === premium) return;
      this.loadedFor = premium;
      void this.load(premium);
    });
  }

  private async load(premium: boolean): Promise<void> {
    // Un invité n'a ni jardin ni conseils : les deux routes lui sont fermées
    // côté serveur. Appeler quand même ne produirait qu'un 403 dans le journal
    // d'incidents, et une carte vide de toute façon.
    if (this.authApi.isGuest()) {
      this.days = [];
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    try {
      if (premium) {
        // La semaine se suffit à elle-même : on l'affiche dès qu'elle arrive.
        // `getToday()` est l'appel le plus lourd de l'app — météo, lune, et
        // parfois une génération par le modèle.
        void this.loadAdviceExtras();
        try {
          this.days = await this.loadWeek();
        } catch {
          // Adresse sans coordonnées, réseau : un abonné doit au moins voir SA
          // journée. Mieux vaut la vue réduite qu'une carte vide.
          this.days = await this.buildFallbackWeek();
        }
      } else {
        // Sans abonnement, le jour courant ne peut venir que du payload des
        // conseils : l'endpoint de la semaine est réservé.
        const advice = await this.adviceApi.getToday();
        this.matchedCount = advice?.lunar?.matchedPlantsCount ?? 0;
        this.days = this.buildFreeWeek(advice?.lunar);
      }
    } catch {
      this.days = [];
    } finally {
      this.isLoading = false;
      this.selectToday();
    }
  }

  /**
   * Compléments d'affichage : cultures concernées aujourd'hui.
   *
   * Chargés APRÈS la semaine et sans la bloquer — ils enrichissent une carte
   * déjà lisible. Un échec ne prive de rien d'essentiel.
   */
  private async loadAdviceExtras(): Promise<void> {
    try {
      const advice = await this.adviceApi.getToday();
      this.matchedCount = advice?.lunar?.matchedPlantsCount ?? 0;
    } catch {
      // Information d'appoint : silence volontaire.
    }
  }

  /** Repli : la semaine gratuite, construite depuis le payload des conseils. */
  private async buildFallbackWeek(): Promise<LunarDayView[]> {
    try {
      const advice = await this.adviceApi.getToday();
      return this.buildFreeWeek(advice?.lunar);
    } catch {
      return [];
    }
  }

  private selectToday(): void {
    this.selected = this.days.find((d) => d.isToday) ?? this.days[0] ?? null;
    this.daySelected.emit(this.selected);
  }

  get hasContent(): boolean {
    return this.days.length > 0;
  }

  select(day: LunarDayView): void {
    if (day.locked) {
      this.premiumPaywallOpen = true;
      return;
    }
    this.selected = day;
    this.daySelected.emit(day);
  }

  /**
   * Vers le détail : la carte nomme le jour, le détail donne les gestes.
   *
   * Le jour est transmis en paramètre : annoncer « prochain jour racines :
   * mer 29 » puis ouvrir un écran positionné sur aujourd'hui serait une
   * promesse non tenue.
   */
  openDetail(date?: string): void {
    void this.router.navigate(['/lunar'], {
      queryParams: date ? { day: date } : {},
    });
  }

  openPaywall(): void {
    this.premiumPaywallOpen = true;
  }

  /**
   * Prochain jour racines visible, ou à défaut le prochain jour d'influence.
   *
   * Les racines sont l'exemple le plus parlant : c'est le jour que les
   * jardiniers guettent pour les semis de carottes et les récoltes à conserver.
   */
  get nextNotable(): LunarDayView | null {
    const upcoming = this.days.filter(
      (d) => !d.isToday && !d.locked && !d.neutral && d.influence,
    );
    return upcoming.find((d) => d.influence === 'root') ?? upcoming[0] ?? null;
  }

  private async loadWeek(): Promise<LunarDayView[]> {
    const calendar = await this.lunarApi.getWeek();
    return (calendar?.items ?? []).map((item) => this.toView(item, false));
  }

  private buildFreeWeek(lunar?: any): LunarDayView[] {
    if (!lunar?.influence) return [];
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      const iso = this.isoOf(date);
      return i === 0
        ? this.toView(
            {
              date: iso,
              influence: lunar.influence,
              moonPath: lunar.moonPath,
              moonPhase: lunar.moonPhase,
              events: lunar.events ?? [],
            } as any,
            false,
          )
        : this.toView({ date: iso, events: [] } as any, true);
    });
  }

  private toView(
    item: Partial<LunarDayResponse>,
    locked: boolean,
  ): LunarDayView {
    const date = new Date(item.date + 'T12:00:00');
    const events = (item.events as unknown as string[]) ?? [];
    // `toLocaleDateString` avec la langue ACTIVE, et non le pipe `date` : ce
    // dernier suit `LOCALE_ID`, laissé à `en-US` faute de configuration — la
    // date sortait en anglais dans une app par ailleurs francophone.
    const locale = this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'fr';
    return {
      date: item.date,
      weekday: date.toLocaleDateString(locale, { weekday: 'short' }),
      dayNumber: String(date.getDate()),
      fullLabel: date.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }),
      influence: (item.influence as Influence) ?? null,
      moonPath: (item.moonPath as string) ?? '',
      moonPhase: (item.moonPhase as string) ?? '',
      moonIllumination: item.moonIllumination ?? null,
      events,
      neutral: events.some((e) => NEUTRAL_EVENTS.includes(e)),
      isToday: item.date === this.isoOf(new Date()),
      locked,
    };
  }

  /** Date locale, jamais `toISOString` : le fuseau décalerait d'un jour. */
  private isoOf(date: Date): string {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${m}-${d}`;
  }
}
